import { danger } from 'redux-store/reducers/common-reducer';

import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';
import { createReducer } from 'utils/redux';
import { __t, applyTranslations } from 'utils/language';

import type { Action, DeliverableAttachment, Dispatch, PaginationMeta, ThunkAction } from 'types';

applyTranslations(require('./inspirationalCampaignAssets.i18n.json'));

const RESET_CONTENT = 'campaignAssets/RESET_CONTENT';
const ADD_CONTENT = 'campaignAssets/ADD_CONTENT';
const SET_CONTENT_IS_LOADING = 'campaignAssets/SET_CONTENT_LOADING';

type State = {
  content: Array<DeliverableAttachment>,
  currentPage: number,
  hasMorePages: boolean,
  isLoading: boolean
};

const initialState: State = {
  content: [],
  currentPage: 1,
  hasMorePages: false,
  isLoading: false
};

const CAMPAIGN_ASSETS_PER_PAGE = 35;

export function resetAssets(): Action {
  return {
    type: RESET_CONTENT
  };
}

function setLoadingStatus(isLoading: boolean): Action {
  return {
    type: SET_CONTENT_IS_LOADING,
    payload: {
      isLoading
    }
  };
}

function addContent(
  content: Array<DeliverableAttachment>,
  currentPage: number,
  hasMorePages: boolean
): Action {
  return {
    type: ADD_CONTENT,
    payload: {
      content,
      currentPage,
      hasMorePages
    }
  };
}


type APICampaignAssetsResponse = {
  data: Array<DeliverableAttachment>,
  meta: PaginationMeta
};

async function getCampaignAssets(campaignId: number, page: number): Promise<APICampaignAssetsResponse> {
  const baseUrl = API.END_POINTS.CAMPAIGNS.getContentApproval(campaignId);
  const queryString = Querystring.objectToString(
    snakeCaseKeys({
      page,
      approvalStatus: 'approved',
      itemsPerPage: CAMPAIGN_ASSETS_PER_PAGE
    })
  );

  const { data } = await get(`${baseUrl}?${queryString}`);
  return data;
}

export function fetchCampaignAssets(campaignId: number, page: number = 1): ThunkAction {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    try {
      const { data, meta } = await getCampaignAssets(campaignId, page);

      dispatch(addContent(data, Number(meta.currentPage), Number(meta.currentPage) < Number(meta.pagesTotal)));
    } catch (err) {
      dispatch(danger(__t('inspirational_campaign_assets_reducer.content_fetch_failed')));
      logError(err);
    } finally {
      dispatch(setLoadingStatus(false));
    }
  };
}

export default createReducer<State, any>('inspirationalCampaignAssets', initialState, {
  [RESET_CONTENT]: () => initialState,
  [ADD_CONTENT]: (state, { content, currentPage, hasMorePages }) => {
    const newContent = (currentPage === 1)
      ? content
      : state.content.concat(content);

    return {
      ...state,
      content: newContent,
      currentPage,
      hasMorePages,
      isLoading: false
    };
  },
  [SET_CONTENT_IS_LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  })
});
